/**
 * The field job screen's Tickets and Change orders sections (EW-5). SNAKE_CASE.
 *
 * Tickets: the job's field tickets, and "Build today's ticket" (POST
 * /earthworks/field/jobs/:id/tickets — the API tickets today for a per_day job,
 * the whole job for a per_job one). A live ticket that already exists (409
 * ticket_exists) is opened rather than reported. No prices are drawn here —
 * a field hand's read carries none, and the list never needed them.
 * Change orders: the job's change orders; the office raises them.
 */
import { useNavigate, Link } from 'react-router-dom';
import { FilePlus2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateEwFieldTicket, useEwFieldChangeOrders, useEwFieldTickets } from '../../../hooks/useEwTickets';
import { ewApiError, ewDay, ewRefusal } from '../../../lib/earthworks';
import { ewCoSkin, ewTicketSkin } from '../../../lib/ew-tickets';
import {
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldQueryState,
  PtFieldSectionTitle,
} from '../../pressure-testing/field/PtFieldBits';

export function EwFieldBillingSections({ jobId, jobStatus }: { jobId: string; jobStatus: string }) {
  const navigate = useNavigate();
  const tickets = useEwFieldTickets(jobId);
  const cos = useEwFieldChangeOrders(jobId);
  const create = useCreateEwFieldTicket();
  const tRows = tickets.data ?? [];
  const cRows = cos.data ?? [];
  const mayBuild = jobStatus === 'in_progress' || jobStatus === 'completed';

  const build = () =>
    create.mutate(jobId, {
      onSuccess: (t) => {
        toast.success(t?.ticket_number ? `${t.ticket_number} drafted` : 'Ticket drafted');
        if (t?.id) navigate(`/field/ew/tickets/${t.id}`);
      },
      onError: (err) => {
        const e = ewApiError(err);
        const existing = typeof e.extra.existingId === 'string' ? e.extra.existingId : null;
        if (e.code === 'ticket_exists' && existing) {
          navigate(`/field/ew/tickets/${existing}`);
          return;
        }
        toast.error(ewRefusal(err, 'The ticket could not be built.'));
      },
    });

  return (
    <>
      <section data-testid="ew-field-job-tickets">
        <PtFieldSectionTitle>Tickets · {tRows.length}</PtFieldSectionTitle>
        <PtFieldQueryState query={tickets} subject="the tickets">
          <PtFieldCard>
            {tRows.length === 0 ? (
              <p className="px-4 py-3 text-sm text-fieldiq-muted">No tickets on this job yet.</p>
            ) : (
              tRows.map((t) => (
                <Link key={t.id} to={`/field/ew/tickets/${t.id}`} data-testid="ew-field-ticket-row" className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-3 last:border-b-0">
                  <span className="min-w-0 text-sm">
                    <span className="block font-mono font-semibold text-white">{t.ticket_number}</span>
                    <span className="block text-xs text-fieldiq-muted">{t.mode === 'per_day' ? ewDay(t.work_date) : t.mode_label || 'Whole job'} · {t.line_count} line{t.line_count === 1 ? '' : 's'}</span>
                  </span>
                  <PtFieldBadge skin={ewTicketSkin(t.status)} />
                </Link>
              ))
            )}
          </PtFieldCard>
        </PtFieldQueryState>
        {mayBuild && (
          <button type="button" className={`${PT_FIELD_SECONDARY} mt-3`} onClick={build} disabled={create.isPending} data-testid="ew-field-build-ticket">
            <FilePlus2 className="size-5" aria-hidden="true" />
            {create.isPending ? 'Building…' : 'Build ticket for sign-off'}
          </button>
        )}
      </section>

      <section data-testid="ew-field-job-cos">
        <PtFieldSectionTitle>Change orders · {cRows.length}</PtFieldSectionTitle>
        <PtFieldQueryState query={cos} subject="the change orders">
          <PtFieldCard>
            {cRows.length === 0 ? (
              <p className="px-4 py-3 text-sm text-fieldiq-muted">No change orders on this job.</p>
            ) : (
              cRows.map((c) => (
                <Link key={c.id} to={`/field/ew/change-orders/${c.id}`} data-testid="ew-field-co-row" className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-3 last:border-b-0">
                  <span className="min-w-0 text-sm">
                    <span className="block font-mono font-semibold text-white">{c.co_number}</span>
                    <span className="block truncate text-xs text-fieldiq-muted">{c.reason}</span>
                  </span>
                  <PtFieldBadge skin={ewCoSkin(c.status)} />
                </Link>
              ))
            )}
          </PtFieldCard>
        </PtFieldQueryState>
      </section>
    </>
  );
}
